import Chart from "react-apexcharts";
import { PieChart } from "react-minimal-pie-chart";
import { Link, useParams } from "react-router-dom";

import { WidgetCategoriesStats } from "~/api/Api";
import { routes } from "~/router";

import { formatNumber } from "../../../../helpers/helpers";
import NoData from "../../../containers/NoData";
import Button from "../../../mantine/button/Button";
import Card from "../../../ui/card/Card";
import Spinner from "../../../ui/spinner/Spinner";
import { SiteStats } from "../../types";
import styles from "./Automations.module.scss";

const recommendationsData = (siteStats: SiteStats) => [
  {
    feature: "Approved",
    quantity: siteStats?.recommendations.active,
    share: siteStats?.recommendations?.active
      ? `${Math.round((siteStats?.recommendations?.active * 100) / siteStats?.recommendations?.total)}%`
      : "0%",
    color: "var(--functional-primary)",
  },
  {
    feature: "Not approved",
    quantity:
      siteStats.recommendations.total - siteStats.recommendations.active,
    share: siteStats.recommendations.total
      ? `${Math.round(((siteStats.recommendations.total - siteStats.recommendations.active) * 100) / siteStats?.recommendations?.total)}%`
      : "0%",
    color: "var(--functional-grey3)",
  },
];
const pageOptimationData = (siteStats: SiteStats) => [
  {
    feature: "Optimized",
    quantity: siteStats?.pages?.optimized,
    share: `${Math.round((siteStats?.pages?.optimized * 100) / siteStats?.pages?.total)}%`,
    color: "var(--functional-primary)",
    chartValue: Math.round(
      (siteStats?.pages?.optimized * 100) / siteStats?.pages?.total,
    ),
  },
  {
    feature: "Pending",
    quantity: siteStats?.pages?.total - siteStats?.pages?.optimized,
    share: `${Math.round(((siteStats?.pages?.total - siteStats?.pages?.optimized) * 100) / siteStats?.pages?.total)}%`,
    color: "var(--functional-grey3)",
    chartValue: Math.round(
      ((siteStats?.pages?.total - siteStats?.pages?.optimized) * 100) /
        siteStats?.pages?.total,
    ),
  },
];
const headers = ["Category", "Number", "Share %"];

const chartData = (data: WidgetCategoriesStats) => {
  const categories = Object.keys(data).map((key) => data[key].category_name);
  const totalValues = Object.keys(data).map((key) => data[key].appearances);
  const approvedValues = Object.keys(data).map(
    (key) => data[key].approved_appearances,
  );

  return {
    series: [
      {
        name: "Total",
        data: totalValues,
      },
      {
        name: "Approved",
        data: approvedValues,
      },
    ],
    options: {
      chart: {
        type: "bar" as const,
        height: 350,
        toolbar: {
          show: false,
        },
      },
      colors: ["#047BF8", "#82DA97"],
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "11px",
        },
      },
      grid: {
        show: true,
        borderColor: "var(--functional-grey3)",
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 1,
        colors: ["transparent"],
      },
      xaxis: {
        lines: {
          show: false,
        },
        labels: {
          show: true,
          style: {
            colors: "var(--text-black)",
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        show: true,
        categories: categories,
      },
      yaxis: {
        lines: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          style: {
            fontSize: "12px",
            colors: "var(--text-black)",
            fontFamily: "Lato, sans-serif",
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          show: true,
        },
      },

      fill: {
        opacity: 1,
      },
      legend: {
        labels: {
          colors: "var(--text-black)",
        },
      },
      tooltip: {
        theme: "dark",
        style: {
          fontSize: "12px",
          fontFamily: "Lato, sans-serif",
        },
        y: {
          formatter: function (
            _val: number,
            {
              seriesIndex,
              dataPointIndex,
            }: { seriesIndex: number; dataPointIndex: number },
          ) {
            const actualValue =
              seriesIndex === 0
                ? totalValues[dataPointIndex]
                : approvedValues[dataPointIndex];

            return formatNumber(actualValue);
          },
        },
      },
    },
  };
};
interface AutomationsProps {
  uniqueRecommendations: WidgetCategoriesStats;
  isUniqueRecommendationsLoading: boolean;
  siteStats: SiteStats | undefined;
  isSiteStatsLoading: boolean;
}
const Automations = ({
  uniqueRecommendations,
  isUniqueRecommendationsLoading,
  siteStats,
  isSiteStatsLoading,
}: AutomationsProps) => {
  const { id } = useParams<{ id: string }>();

  const isNoRecommendations =
    siteStats?.recommendations?.total === 0 &&
    siteStats?.recommendations?.active === 0;
  const isNoPagesOptimized =
    siteStats?.pages?.total === 0 && siteStats?.pages?.optimized === 0;

  return (
    <Card title="Automations">
      <div className={styles.automations}>
        <div className={styles.containerFlex}>
          <div className={styles.container}>
            <div className={styles.containerHeader}>
              <div className={styles.containerHeaderTitle}>Recommendations</div>
              {id && (
                <Button
                  variant="outlinedIconSmall"
                  component={Link}
                  to={routes.site.onpage({ id })}
                >
                  View
                </Button>
              )}
            </div>
            <div className={styles.charts}>
              {isSiteStatsLoading || !siteStats ? (
                <Spinner />
              ) : (
                <>
                  {isNoRecommendations ? (
                    <NoData title="No Recommendations" />
                  ) : (
                    <div className={styles.chartsContent}>
                      <PieChart
                        startAngle={270}
                        lineWidth={15}
                        data={recommendationsData(siteStats)?.map((item) => {
                          return {
                            title: item.feature,
                            value: item.quantity,
                            color: !item.quantity ? "grey" : item.color,
                          };
                        })}
                      />
                      <div className={styles.chartsLegend}>
                        <div className={styles.chartsLegendHeader}>
                          {headers.map((header, index) => (
                            <span key={index}>{header}</span>
                          ))}
                        </div>
                        {recommendationsData(siteStats).map((item, index) => (
                          <div key={index} className={styles.chartsLegendItem}>
                            <div
                              className={styles.chartsLegendColor}
                              style={{ backgroundColor: item.color }}
                              aria-hidden="true"
                            />
                            <div className={styles.chartsLegendText}>
                              <span>{item.feature}</span>
                              <span>{formatNumber(item.quantity)}</span>
                              <span>{item.share}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className={styles.container}>
            <div className={styles.containerHeader}>
              <div className={styles.containerHeaderTitle}>
                Pages Optimization
              </div>
              {id && (
                <Button
                  variant="outlinedIconSmall"
                  component={Link}
                  to={routes.site.pages({ id })}
                >
                  View
                </Button>
              )}
            </div>
            <div className={styles.charts}>
              {isSiteStatsLoading || !siteStats ? (
                <Spinner />
              ) : (
                <>
                  {isNoPagesOptimized ? (
                    <NoData title="No Pages Optimized" />
                  ) : (
                    <div className={styles.chartsContent}>
                      <PieChart
                        startAngle={270}
                        lineWidth={15}
                        data={pageOptimationData(siteStats)?.map((item) => {
                          return {
                            title: item.feature,
                            value: item.quantity,
                            color: !item.quantity ? "grey" : item.color,
                          };
                        })}
                      />
                      <div className={styles.chartsLegend}>
                        <div className={styles.chartsLegendHeader}>
                          {headers.map((header, index) => (
                            <span key={index}>{header}</span>
                          ))}
                        </div>
                        {pageOptimationData(siteStats)?.map((item, index) => (
                          <div key={index} className={styles.chartsLegendItem}>
                            <div
                              className={styles.chartsLegendColor}
                              style={{ backgroundColor: item.color }}
                              aria-hidden="true"
                            />
                            <div className={styles.chartsLegendText}>
                              <span>{item.feature}</span>
                              <span>{formatNumber(item.quantity)}</span>
                              <span>{item.share}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className={styles.container}>
          <div className={styles.containerTitle}>
            Recommendation Appearances
          </div>
          <div className={styles.containerSubtitle}>
            Recommendation Type to Number
          </div>
          {isUniqueRecommendationsLoading ? (
            <Spinner />
          ) : (
            <>
              {Object.keys(uniqueRecommendations)?.length ? (
                <div className={styles.chartWrapper}>
                  <Chart
                    options={chartData(uniqueRecommendations)?.options}
                    series={chartData(uniqueRecommendations)?.series}
                    type="bar"
                    height={390}
                  />
                </div>
              ) : (
                <NoData title="No Unique Recommendation Appearances" />
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};
export default Automations;
